/**
 * Read the reason out of a failed API response, for the toast.
 *
 * Why this exists: every one of our write routes already answers a rejection
 * with a sentence a plumber can act on — "Line item quantity must be greater
 * than 0", "Invalid client_id for this workspace." — and every caller was
 * throwing it away:
 *
 *     } else { toast('Failed to create invoice.', 'error'); }
 *
 * On 8 September 2026 that cost one plumbing firm a morning. Their office manager tried
 * to raise an invoice and then a quote for one customer; both came back HTTP
 * 400 from the line-item validator, both said exactly what was wrong, and both
 * times the screen said "Failed to create invoice." She retried, changed
 * nothing that mattered, and gave up — while the answer had been sent to her
 * browser four times.
 *
 * A generic failure toast is worse than no toast: it tells the owner the
 * product is broken when in fact the product is telling them something.
 *
 * The fallback is still needed — a 500 from an unhandled throw returns HTML,
 * and a network-level failure returns nothing parseable — but it is now the
 * last resort rather than the only outcome.
 */

/** Longest server sentence we will put in a toast before falling back. */
const MAX_TOAST_MESSAGE = 200;

/**
 * The message to show for a non-ok `Response`. Never throws, never rejects:
 * a body that is missing, HTML, malformed JSON, or carries no `error` string
 * yields `fallback`.
 *
 * 🚨 Consumes the response body. Call it only on the failure branch, where
 * nothing else is going to read it.
 */
export async function apiErrorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body: unknown = await res.json();
    if (body && typeof body === 'object') {
      const raw = (body as { error?: unknown }).error;
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        // An over-long or empty string is a stack trace or a stray template,
        // not a sentence written for this reader — take the fallback instead.
        if (trimmed.length > 0 && trimmed.length <= MAX_TOAST_MESSAGE) return trimmed;
      }
    }
  } catch {
    // Not JSON (HTML error page, empty body, aborted read) — fall through.
  }
  return fallback;
}

/**
 * Everything `useReportFailure()` needs from a failed `Response`, in one
 * read. Unlike `apiErrorMessage` it keeps the whole body — the `action` and
 * `title` a route attaches when there is something the owner can do — and
 * does not length-cap the sentence, because a notice has room for three.
 *
 * 🚨 Consumes the response body, like `apiErrorMessage`.
 */
export async function readFailure(
  res: Response,
  fallback: string,
  request?: string,
): Promise<{ status: number; body: unknown; fallback: string; request?: string }> {
  let body: unknown = undefined;
  try {
    body = await res.json();
  } catch {
    // Not JSON (HTML error page, empty body) — the fallback will be used.
  }
  return { status: res.status, body, fallback, request };
}
